import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {JsonService, SessionStorageService} from '@dcp/configurator';
import {EventService, LogoutEvent} from '@spartacus/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

export const customerIdParameterName: string = 'customerID';
export const companyNameParameterName: string = 'companyName';
export const streetParameterName: string = 'street';
export const streetLine2ParameterName: string = 'line2';
export const cityParameterName: string = 'city';
export const postalCodeParameterName: string = 'postalCode';
export const countryParameterName: string = 'countryCode';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})

export class CrmAccountTrackingService {
  // Expiration time is in milliseconds (1hour)
  expirationTime: number = 60 * 60 * 1000;

  constructor(
    private eventService: EventService,
    private jsonService: JsonService,
    private sessionStorageService: SessionStorageService,
    @Inject(PLATFORM_ID) public platformId: object,
  ) {
    this.subscribeToEventService();
  }

  subscribeToEventService() {
    this.eventService.get(LogoutEvent).pipe(untilDestroyed(this)).subscribe(() => {
      this.clearAllData();
    });
  }

  /**
   * Remove all the saved data from the session storage with its value.
   */
  public clearAllData(): void {
    this.getAllParametersArray().forEach(parameterName =>
      this.clearData(parameterName)
    );
  }

  /**
   * Remove the session data associated with the name of the parameter.
   *
   * @param parameterName name of the parameter to clear the session data associated with it.
   */
  public clearData(parameterName: string): void {
    this.sessionStorageService.removeItem(parameterName);
  }

  /**
   * Save the 'parameterValue' under the 'parameterName' in the session storage when DCP site is entered
   * with a specific link containing this parameter.
   */
  public saveInSessionStorage(parameterName: string, parameterValue: string): void {
    const expires: Date = this.createExpireDate();
    const sessionData: any = {
      expires: expires.toUTCString(),
      data: parameterValue
    };
    this.sessionStorageService.setItem(parameterName, JSON.stringify(sessionData));
  }

  /**
   * Get the value of the parameter by its name stored in the session storage.
   *
   * @param parameterName name of the parameter to get the value of.
   */
  public getDataFromSessionStorage(parameterName: string): string {
    if (this.sessionDataExists(parameterName)) {
      const sessionData: any = this.jsonService.parseSafely(this.sessionStorageService.getItem(parameterName));
      if (Date.parse(sessionData.expires) > new Date().getTime()) {
        return sessionData.data;
      } else {
        this.clearData(parameterName);
      }
    }
    return null;
  }

  /**
   * returns all the parameter names inside an array
   */
  public getAllParametersArray(): string[] {
    return [
      customerIdParameterName,
      companyNameParameterName,
      streetParameterName,
      streetLine2ParameterName,
      cityParameterName,
      postalCodeParameterName,
      countryParameterName
    ];
  }

  /**
   * Sets up the value for all parameters
   */
  public setUpTrackingForAllParameters(): void {
    if (isPlatformBrowser(this.platformId)) {
      const query: string = window.location.search;
      if (query) {
        const allParametersArray = this.getAllParametersArray();
        allParametersArray.forEach(parameterName => {
          const valueFromUrl: string = this.readValueFromUrl(query, parameterName);
          if (valueFromUrl) {
            this.saveInSessionStorage(parameterName, valueFromUrl);
          }
        });
      }
    }
  }

  /**
   * Searches the value of the parameter by its name from query and returns it.
   *
   * @param query to search the parameters in.
   * @param parameterName name of the parameter to search for.
   */
  readValueFromUrl(query: string, parameterName: string): string {
    const searchParams: URLSearchParams = new URLSearchParams(query);
    return searchParams.get(parameterName);
  }

  /**
   * Expiring the 'customerId' parameter in the session storage after 1 hour passes.
   */
  createExpireDate() {
    const expires: Date = new Date();
    expires.setTime(expires.getTime() + this.expirationTime);
    return expires;
  }

  /**
   * Checking if there is any value present currently for 'customerId' parameter
   * in the session storage.
   *
   * @private
   */
  private sessionDataExists(parameterName: string): boolean {
    return isPlatformBrowser(this.platformId) && window.sessionStorage
      && !!this.sessionStorageService.getItem(parameterName);
  }
}
